import { useContext, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import Drawer from '../Drawer';
import Flex from '../Flex';
import Icon, { registerIcon } from '../Icon';
import * as arrowLeftIcon from '../Icon/icons/arrow-left.icon';
import Text from '../Text';
import {
  useConsolidatedRef,
  useDirection,
  useElement,
  useEscape,
  useFocusTrap,
  useI18n,
  useTheme,
  useUID
} from '../../hooks';
import { getActiveElement } from '../../utils';
import { isSolidColor } from '../../styles';
import Button from '../Button';
import Configuration from '../Configuration';

import AppShellContext from './AppShellContext';
import {
  StyledDrawerWrapper,
  StyledDrawerHeading,
  StyledDrawerListContainer,
  StyledTopNavDrawerOffset
} from './Drawer.styles';
import { navContrastColors } from './style-utils';

registerIcon(arrowLeftIcon);

interface DrawerViewProps {
  content: ReactNode;
  header: string;
  onDrawerOpen?: () => void;
  onDrawerClose?: () => void;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  topOffset: string;
}

export default ({
  content,
  header,
  onDrawerOpen,
  onDrawerClose,
  drawerOpen,
  setDrawerOpen,
  topOffset
}: DrawerViewProps) => {
  const t = useI18n();
  const theme = useTheme();
  const { focusedImperatively } = useContext(AppShellContext);
  const { start } = useDirection();
  const returnFocusRef = useRef<HTMLElement>();
  const [drawerEl, setDrawerEl] = useElement<HTMLDivElement>();
  const drawerRef = useConsolidatedRef(setDrawerEl);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const headerId = useUID();

  useEscape(
    () => {
      setDrawerOpen(false);
    },
    drawerEl,
    [drawerEl]
  );

  useFocusTrap(drawerRef);

  const { foreground, interactive, urgent } = navContrastColors(theme);
  const navBg = theme.components['app-shell'].nav.background;
  const navTheme = {
    base: {
      palette: {
        'primary-background': isSolidColor(navBg) ? navBg : 'transparent',
        'foreground-color': foreground,
        interactive,
        urgent
      }
    }
  };

  // Using layout effect to capture the focused element prior to drawer elements taking focus.
  useLayoutEffect(() => {
    returnFocusRef.current = (getActiveElement() as HTMLElement) ?? undefined;
  }, []);

  const drawerContent = (
    <Drawer
      role='dialog'
      aria-labelledby={headerId}
      as={StyledDrawerWrapper}
      position='fixed'
      topOffset={topOffset}
      placement={start}
      open={drawerOpen}
      nullWhenClosed
      onAfterOpen={() => {
        if (backButtonRef.current && !drawerRef.current?.contains(getActiveElement())) {
          focusedImperatively.current = true;
          backButtonRef.current.focus();
        }
        onDrawerOpen?.();
      }}
      onAfterClose={() => {
        onDrawerClose?.();
        returnFocusRef.current?.focus();
      }}
      onOuterClick={() => setDrawerOpen(false)}
      ref={drawerRef}
    >
      <Flex as={StyledDrawerHeading} role='none' container={{ alignItems: 'center' }}>
        <Button
          ref={backButtonRef}
          icon
          variant='simple'
          onClick={() => setDrawerOpen(false)}
          label={t('close')}
          aria-label={t('close_noun', [header])}
        >
          <Icon name={`arrow-${start}`} />
        </Button>
        <Text variant='h2' id={headerId}>
          {header}
        </Text>
      </Flex>
      <Flex
        as={StyledDrawerListContainer}
        container={{ direction: 'column' }}
        aria-labelledby={headerId}
      >
        {content}
      </Flex>
    </Drawer>
  );

  return (
    <Configuration theme={navTheme} portalTarget={drawerEl ?? undefined}>
      {topOffset ? (
        <StyledTopNavDrawerOffset $topOffset={topOffset}>{drawerContent}</StyledTopNavDrawerOffset>
      ) : (
        drawerContent
      )}
    </Configuration>
  );
};
